import React from 'react';
import { TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { KPICard, CustomTooltip, StatementHeader, SectionRow } from './ReportHelpers';

export function BalanceSheetReport({ reportData, viewMode, entityName, includePY }) {
  if (!reportData || !reportData.assets) return null;
  const { assets, liabilities, equity, is_balanced } = reportData;
  const py = reportData.prior_year;
  const hasPY = includePY && !!py;

  if (viewMode === 'list') {
    return (
      <Card data-testid="bs-list-view">
        <CardContent className="py-8 px-6 max-w-3xl mx-auto">
          <StatementHeader entityName={entityName} title="Balance Sheet" asOf={reportData.as_of_date} />
          {hasPY && <p className="text-xs text-zinc-400 text-center mb-4">Prior Year: FY {py.fiscal_year}</p>}
          <table className="w-full text-sm">
            {hasPY && (
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="py-2 px-4 text-left text-xs text-zinc-500"></th>
                  <th className="py-2 px-4 text-right text-xs text-zinc-500 font-semibold">Current</th>
                  <th className="py-2 px-4 text-right text-xs text-zinc-400 font-semibold pr-6">PY ({py.fiscal_year})</th>
                </tr>
              </thead>
            )}
            <tbody>
              <SectionRow label="ASSETS" bold />
              {assets.accounts.map(a => <SectionRow key={a.code} label={`${a.code}  ${a.name}`} amount={a.balance} pyAmount={hasPY ? (a.py_balance || 0) : undefined} indent />)}
              <SectionRow label="Total Assets" amount={assets.total} pyAmount={hasPY ? py.total_assets : undefined} total />
              <tr><td className="py-3" colSpan={hasPY ? "3" : "2"}></td></tr>
              <SectionRow label="LIABILITIES" bold />
              {liabilities.accounts.map(a => <SectionRow key={a.code} label={`${a.code}  ${a.name}`} amount={a.balance} pyAmount={hasPY ? (a.py_balance || 0) : undefined} indent />)}
              <SectionRow label="Total Liabilities" amount={liabilities.total} pyAmount={hasPY ? py.total_liabilities : undefined} total />
              <tr><td className="py-3" colSpan={hasPY ? "3" : "2"}></td></tr>
              <SectionRow label="EQUITY" bold />
              {equity.accounts.map(a => <SectionRow key={a.code} label={`${a.code}  ${a.name}`} amount={a.balance} pyAmount={hasPY ? (a.py_balance || 0) : undefined} indent />)}
              <SectionRow label="Retained Earnings" amount={equity.retained_earnings} pyAmount={hasPY ? py.retained_earnings : undefined} indent />
              <SectionRow label="Total Equity" amount={equity.total} pyAmount={hasPY ? py.total_equity : undefined} total />
              <tr><td className="py-3" colSpan={hasPY ? "3" : "2"}></td></tr>
              <SectionRow label="TOTAL LIABILITIES & EQUITY" amount={reportData.total_liabilities_and_equity} pyAmount={hasPY ? py.total_liabilities_and_equity : undefined} grandTotal />
            </tbody>
          </table>
          <div className={`mt-4 text-center text-xs font-semibold ${is_balanced !== false ? 'text-emerald-600' : 'text-red-600'}`}>
            {is_balanced !== false ? 'Assets = Liabilities + Equity' : 'WARNING: Balance Sheet is not balanced'}
          </div>
        </CardContent>
      </Card>
    );
  }

  const summaryData = [
    { name: 'Assets', value: assets.total },
    { name: 'Liabilities', value: liabilities.total },
    { name: 'Equity', value: equity.total },
  ];

  const renderAccountSection = (title, sectionAccounts, total, color, pyTotal) => (
    <div>
      <h3 className="text-sm font-semibold text-zinc-500 mb-2 uppercase tracking-wider">{title}</h3>
      <table className="report-table">
        {hasPY && <thead><tr><th className="pl-4">Code</th><th>Account</th><th className="text-right">Current</th><th className="text-right pr-4 text-zinc-400">PY</th></tr></thead>}
        <tbody>
          {sectionAccounts.map(a => (
            <tr key={a.code}>
              <td className="font-mono text-zinc-400 w-16 pl-4">{a.code}</td>
              <td>{a.name}</td>
              <td className="text-right font-mono w-32 pr-4">{formatCurrency(a.balance)}</td>
              {hasPY && <td className="text-right font-mono w-32 pr-4 text-zinc-400">{formatCurrency(a.py_balance || 0)}</td>}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-zinc-200">
            <td colSpan="2" className="font-semibold pl-4">Total {title}</td>
            <td className={`text-right font-mono font-semibold pr-4 ${color}`}>{formatCurrency(total)}</td>
            {hasPY && <td className="text-right font-mono font-semibold pr-4 text-zinc-400">{formatCurrency(pyTotal || 0)}</td>}
          </tr>
        </tfoot>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard title="Total Assets" value={formatCurrency(assets.total)} subtitle={hasPY ? `PY: ${formatCurrency(py.total_assets)}` : undefined} icon={TrendingUp} color="green" />
        <KPICard title="Total Liabilities" value={formatCurrency(liabilities.total)} subtitle={hasPY ? `PY: ${formatCurrency(py.total_liabilities)}` : undefined} icon={TrendingDown} color="red" />
        <KPICard title="Total Equity" value={formatCurrency(equity.total)} subtitle={hasPY ? `PY: ${formatCurrency(py.total_equity)}` : undefined} icon={DollarSign} color="blue" />
      </div>

      {summaryData.some(d => d.value !== 0) && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Balance Sheet Overview</CardTitle>
            <Badge className={is_balanced !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>{is_balanced !== false ? 'Balanced' : 'Not Balanced'}</Badge>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summaryData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={v => formatCurrency(v)} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Amount" radius={[6, 6, 0, 0]}>
                    <Cell key="assets" fill="#34D399" />
                    <Cell key="liabilities" fill="#F87171" />
                    <Cell key="equity" fill="#60A5FA" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="py-6 space-y-6">
          {renderAccountSection('Assets', assets.accounts, assets.total, 'text-emerald-600', hasPY ? py.total_assets : 0)}
          {renderAccountSection('Liabilities', liabilities.accounts, liabilities.total, 'text-red-500', hasPY ? py.total_liabilities : 0)}
          <div>
            <h3 className="text-sm font-semibold text-zinc-500 mb-2 uppercase tracking-wider">Equity</h3>
            <table className="report-table">
              {hasPY && <thead><tr><th className="pl-4">Code</th><th>Account</th><th className="text-right">Current</th><th className="text-right pr-4 text-zinc-400">PY</th></tr></thead>}
              <tbody>
                {equity.accounts.map(a => (
                  <tr key={a.code}>
                    <td className="font-mono text-zinc-400 w-16 pl-4">{a.code}</td>
                    <td>{a.name}</td>
                    <td className="text-right font-mono w-32 pr-4">{formatCurrency(a.balance)}</td>
                    {hasPY && <td className="text-right font-mono w-32 pr-4 text-zinc-400">{formatCurrency(a.py_balance || 0)}</td>}
                  </tr>
                ))}
                <tr>
                  <td className="font-mono text-zinc-400 pl-4">-</td>
                  <td className="italic">Retained Earnings</td>
                  <td className="text-right font-mono pr-4">{formatCurrency(equity.retained_earnings)}</td>
                  {hasPY && <td className="text-right font-mono pr-4 text-zinc-400">{formatCurrency(py.retained_earnings)}</td>}
                </tr>
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-zinc-200">
                  <td colSpan="2" className="font-semibold pl-4">Total Equity</td>
                  <td className="text-right font-mono font-semibold pr-4 text-blue-600">{formatCurrency(equity.total)}</td>
                  {hasPY && <td className="text-right font-mono font-semibold pr-4 text-zinc-400">{formatCurrency(py.total_equity)}</td>}
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
